import { PerspectiveCamera, Scene, AudioListener, PositionalAudio, Camera, AudioContext as TAudioContext } from 'three';
import { NoiseCancelationNode } from "./NoiseCancelation";

import trafficAudioUrl from '../assets/bubble_traffic.mp3';
import televisionAudioUrl from '../assets/bubble_television.mp3';
import zoomCallAudioUrl from '../assets/bubble_zoomcall.mp3';

export const ROOM_WIDTH = 500;
export const ROOM_HEIGHT = 500;

let audioCtx: AudioContext | null = null;
let noiseCancelationWetGain: GainNode | null = null;
let noiseCancelationDryGain: GainNode | null = null;
let noiseCancelation: NoiseCancelationNode | null = null;
let lpf: BiquadFilterNode | null = null;
let masterGain: GainNode | null = null;
let scene: Scene | null = null;
let camera: Camera | null = null;
let listener: AudioListener | null = null;
let trafficSource: HTMLAudioElement | null = null;
let traffic: PositionalAudio | null = null;
let televisionSource: HTMLAudioElement | null = null;
let television: PositionalAudio | null = null;
let zoomCallSource: HTMLAudioElement | null = null;
let zoomCall: PositionalAudio | null = null;
let noiseCancelOn = false;

export async function init() {
    audioCtx = new AudioContext();
    await NoiseCancelationNode.init(audioCtx);
    noiseCancelationWetGain = audioCtx.createGain();
    noiseCancelationDryGain = audioCtx.createGain();
    lpf = audioCtx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.value = 500;
    noiseCancelation = new NoiseCancelationNode(audioCtx);
    masterGain = audioCtx.createGain();
    masterGain.gain.value = 0.0;

    noiseCancelationWetGain.connect(lpf);
    lpf.connect(noiseCancelation);
    noiseCancelation.connect(masterGain);
    noiseCancelationDryGain.connect(masterGain);
    masterGain.connect(audioCtx.destination);

    (TAudioContext.setContext as (ctx: AudioContext) => void)(audioCtx);

    scene = new Scene();
    camera = new PerspectiveCamera(75, ROOM_WIDTH / ROOM_HEIGHT, 0.1, 1000);
    scene.add(camera);

    listener = new AudioListener();
    camera.add(listener);
    listener.gain.disconnect();
    listener.gain.connect(noiseCancelationDryGain);
    listener.gain.connect(noiseCancelationWetGain);

    trafficSource = document.createElement('audio');
    trafficSource.src = trafficAudioUrl;
    trafficSource.loop = true;
    traffic = new PositionalAudio(listener);
    traffic.setRefDistance(0.5);
    traffic.setMediaElementSource(trafficSource);
    traffic.position.set(-10, 0, 0);
    traffic.updateMatrixWorld();
    scene.add(traffic);

    televisionSource = document.createElement('audio');
    televisionSource.src = televisionAudioUrl;
    televisionSource.loop = true;
    television = new PositionalAudio(listener);
    television.setRefDistance(0.5);
    television.setMediaElementSource(televisionSource);
    television.position.set(10, 0, 0);
    television.updateMatrixWorld();
    scene.add(television);

    zoomCallSource = document.createElement('audio');
    zoomCallSource.src = zoomCallAudioUrl;
    zoomCallSource.loop = true;
    zoomCall = new PositionalAudio(listener);
    zoomCall.setRefDistance(0.5);
    zoomCall.setMediaElementSource(zoomCallSource);
    zoomCall.position.set(0, 10, 0);
    zoomCall.updateMatrixWorld();
    scene.add(zoomCall);
}


export async function start() {
    if (!audioCtx) await init();
    audioCtx!.resume();
    trafficSource!.play();
    televisionSource!.play();
    zoomCallSource!.play();
}

export function enterRoom() {
    if (masterGain) {
        masterGain.gain.setTargetAtTime(1.0, audioCtx!.currentTime, 0.1);
    }
}

export function leaveRoom() {
    if (masterGain) {
        masterGain.gain.setTargetAtTime(0.0, audioCtx!.currentTime, 0.1);
    }
}

export async function moveTo(x: number, y: number) {
    if (!camera) return;
    camera.position.setComponent(0, -10 + x * 20);
    camera.position.setComponent(1, -10 + x * 20);
    camera.updateMatrixWorld();
}

export async function toggleCanceling(on: boolean) {
    if (on === noiseCancelOn) return;
    if (!audioCtx) await init();
    if (on) {
        noiseCancelationDryGain?.gain.setTargetAtTime(0, audioCtx!.currentTime, 0.1);
        noiseCancelationWetGain?.gain.setTargetAtTime(1, audioCtx!.currentTime, 0.1);
    } else {
        noiseCancelationDryGain?.gain.setTargetAtTime(1, audioCtx!.currentTime, 0.1);
        noiseCancelationWetGain?.gain.setTargetAtTime(0, audioCtx!.currentTime, 0.1);
    }
    noiseCancelOn = on;
}